import Icon from "@ui-kit/icons/Icon.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { enums } from "@shared/constants";
import { getPriceRange } from "@shared/modules/financials/utils/price-utils";
import MembershipRenewalText from "@shared/modules/memberships/components/MembershipRenewalText/MembershipRenewalText.web";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import { SeatingEventPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import { useBrowserTimezone } from "@shared/hooks/useBrowserTimezone";
import { formatDate } from "@shared/utils/dates/formatting";
import { useTranslation } from "@shared/utils/internationalization";

export interface MembershipBadgesProps {
  isSmallerThanSm: boolean;
  membershipGroupData:
    | PublicMembershipGroupSerializer
    | SeatingEventPropertyMap
    | undefined;
}

type MembershipBadge = {
  id: number;
  icon: JSX.Element;
  text: string | JSX.Element;
  showBadge: boolean;
  dataTestId: string;
};

/*
 * @deprecated Use badges from shared/components/badges
 */
const MembershipBadges = ({
  isSmallerThanSm,
  membershipGroupData,
}: MembershipBadgesProps): MembershipBadge[] => {
  const { t } = useTranslation("purchases");
  const browserTimezone = useBrowserTimezone();

  return [
    {
      id: 0,
      icon: (
        <Icon.Calendar
          boxSize={{ base: "element.md", sm: "element.xl" }}
          mr="sm"
          aria-label={t("calendar icon")}
        />
      ),
      text: (
        <Flex direction="column">
          <Text>
            {t("Starts")}
            {": "}
            {membershipGroupData?.current_season_on_sale
              ? formatDate({
                  date: membershipGroupData.current_season_on_sale.start_date,
                  timezone: browserTimezone,
                  format: "EEEE MMMM do, yyyy",
                })
              : ""}
          </Text>
          <Text>
            {t("Ends")}
            {": "}
            {membershipGroupData?.current_season_on_sale
              ? formatDate({
                  date: membershipGroupData?.current_season_on_sale?.end_date,
                  timezone: browserTimezone,
                  format: "EEEE MMMM do, yyyy",
                })
              : ""}
          </Text>
        </Flex>
      ),
      showBadge: Boolean(membershipGroupData?.current_season_on_sale),
      dataTestId: "date",
    },
    {
      id: 1,
      icon: (
        <Icon.Renewal
          boxSize={{ base: "element.md", sm: "element.xl" }}
          mr="sm"
          aria-label={t("renewal icon")}
        />
      ),
      text: (
        <MembershipRenewalText
          renewalFrequency={membershipGroupData?.renewal_frequency}
        />
      ),
      showBadge:
        membershipGroupData?.renewal_frequency !==
        enums.RENEWAL_FREQUENCIES_MAP_BY_ID.one_time.id,
      dataTestId: "renewal",
    },
    {
      id: 2,
      icon: (
        <Icon.Shield
          boxSize={{ base: "element.md", sm: "element.xl" }}
          mr="sm"
          aria-label={t("secure icon")}
        />
      ),
      text: t("100% Secure"),
      showBadge: true,
      dataTestId: "secure",
    },
    {
      id: 3,
      icon: (
        <Icon.Membership
          boxSize={{ base: "element.md", sm: "element.xl" }}
          mr="sm"
          aria-label={t("discount icon")}
        />
      ),
      text: t("Discounts on Events"),
      showBadge: Boolean(membershipGroupData?.benefit_data?.discount?.exists),
      dataTestId: "discount",
    },
    {
      id: 4,
      icon: (
        <Icon.Orders
          boxSize={{ base: "element.md", sm: "element.xl" }}
          mr="sm"
          aria-label={t("confirmation icon")}
        />
      ),
      text: t("Instant Confirmation"),
      showBadge: true,
      dataTestId: "confirmation",
    },
    {
      id: 5,
      icon: (
        <Icon.DollarFilled
          boxSize={{ base: "element.md", sm: "element.xl" }}
          mr="sm"
          aria-label={t("dollar icon")}
        />
      ),
      text: t("Payment Plans Available"),
      showBadge: false, // TODO: Hide this badge until membershipGroupData.has_payment_plans exists
      dataTestId: "price",
    },
    {
      id: 6,
      icon: (
        <Icon.DollarFilled
          boxSize={{ base: "element.md", sm: "element.xl" }}
          mr="sm"
          aria-label={t("dollar icon")}
        />
      ),
      text: getPriceRange({
        items: membershipGroupData?.membership_levels,
        allInPricing: membershipGroupData?.venue?.all_in_pricing_is_enabled,
        venueCurrency: membershipGroupData?.venue?.currency,
      }),
      showBadge: isSmallerThanSm,
      dataTestId: "price-range",
    },
  ];
};

export default MembershipBadges;
